import type { AgentFolder, AgentWithContext } from "./types";
import { agentNeedsYou } from "./waiting";

/**
 * The Agents list's folders, as pure functions: how the server's order is cut
 * into groups, what a drop means, and the optimistic edits a drop makes to the
 * cache before the server answers. The server keeps the same rules (core's
 * `services/agent-folders.ts`): the master is never in a folder, and a folder its
 * last agent leaves is gone.
 */

type ListAgent = Pick<AgentWithContext, "id" | "kind" | "name" | "handle" | "project" | "pinned" | "folderId" | "status" | "openQuestions"> &
  Partial<Pick<AgentWithContext, "link">>;

export interface FolderGroup<A extends ListAgent = AgentWithContext> {
  folder: AgentFolder;
  /** Members shown inside it: not the pinned ones (they show under Pinned), and only matches while searching. */
  members: A[];
  /** Every member, pinned included — the header's count. */
  memberCount: number;
  /** Members shown under Pinned instead. */
  pinnedCount: number;
  /** Members waiting on the user, pinned or not: what a collapsed header must still show. */
  needsYou: number;
  working: number;
  expanded: boolean;
}

export interface AgentListGroups<A extends ListAgent = AgentWithContext> {
  master: A | null;
  pinned: A[];
  folders: FolderGroup<A>[];
  loose: A[];
}

const matches = (agent: ListAgent, needle: string) =>
  // A foreign agent is found by its machine, as a session agent is by its project.
  [agent.name, agent.handle, agent.project?.name ?? (agent.kind === "foreign" ? (agent.link?.machine ?? "") : "workspace")].some(
    (field) => field.toLowerCase().includes(needle),
  );

/**
 * The server's order (`compareAgentsForList`) cut into the list's groups: the
 * master, the pinned, then each folder in its position, then everyone loose.
 * Pinned wins for placement, so nothing is listed twice; the folder still counts
 * it. A search shows a folder when its name or any member matches, opened.
 */
export function groupAgentList<A extends ListAgent>(
  agents: A[],
  folders: AgentFolder[],
  query: string,
  collapsed: ReadonlySet<string>,
): AgentListGroups<A> {
  const needle = query.trim().toLowerCase();
  const known = new Set(folders.map((folder) => folder.id));
  const visible = needle ? agents.filter((agent) => matches(agent, needle)) : agents;
  const master = visible.find((agent) => agent.kind === "master") ?? null;
  const pinned = visible.filter((agent) => agent.kind !== "master" && agent.pinned);
  const groups: FolderGroup<A>[] = [];
  for (const folder of [...folders].sort((a, b) => a.position - b.position)) {
    const all = agents.filter((agent) => agent.kind !== "master" && agent.folderId === folder.id);
    if (all.length === 0) continue;
    const nameMatches = needle !== "" && folder.name.toLowerCase().includes(needle);
    const shown = (nameMatches || !needle ? all : all.filter((agent) => matches(agent, needle))).filter((agent) => !agent.pinned);
    if (needle && !nameMatches && !all.some((agent) => matches(agent, needle))) continue;
    groups.push({
      folder,
      members: shown,
      memberCount: all.length,
      pinnedCount: all.filter((agent) => agent.pinned).length,
      needsYou: all.filter(agentNeedsYou).length,
      working: all.filter((agent) => agent.status === "working").length,
      expanded: needle !== "" || !collapsed.has(folder.id),
    });
  }
  // An agent whose folder the cache does not know yet (a refetch in between) shows loose, not lost.
  const loose = visible.filter((agent) => agent.kind !== "master" && !agent.pinned && (!agent.folderId || !known.has(agent.folderId)));
  return { master, pinned, folders: groups, loose };
}

/**
 * The name a folder made by a drop starts with — a copy of core's
 * `proposeFolderName` (services/agent-folders.ts), so the optimistic folder shows
 * the name the server will give it. The two must agree. Each group is one way of
 * naming the agents (project slugs, project names, handles); the first word all of
 * a group share wins, else "New folder".
 */
export function proposeFolderName(groups: (string | null | undefined)[][]): string {
  for (const names of groups) {
    const words = names.map((name) => (name ?? "").trim().toLowerCase().split(/[\s_.\-/]+/)[0] ?? "");
    const first = words[0];
    if (words.length > 1 && first && first.length > 1 && words.every((word) => word === first)) {
      return first[0]!.toUpperCase() + first.slice(1);
    }
  }
  return "New folder";
}

/** Where a dragged agent can land: on an agent's row, on a folder (header or open body), or the loose list. */
export type FolderDropTarget = { kind: "agent"; id: string } | { kind: "folder"; id: string } | { kind: "loose" };

/** `data-drop` values: `agent:<id>`, `folder:<id>`, `loose`. */
export function parseDropTarget(value: string | undefined | null): FolderDropTarget | null {
  if (!value) return null;
  if (value === "loose") return { kind: "loose" };
  const [kind, id] = [value.slice(0, value.indexOf(":")), value.slice(value.indexOf(":") + 1)];
  if ((kind === "agent" || kind === "folder") && id) return { kind, id };
  return null;
}

export const dropTargetKey = (target: FolderDropTarget | null): string | null =>
  target ? (target.kind === "loose" ? "loose" : `${target.kind}:${target.id}`) : null;

export type FolderDrop =
  | { kind: "none" }
  | { kind: "move"; agentId: string; folderId: string | null }
  | { kind: "create"; agentIds: [string, string] };

/**
 * What letting go of `draggedId` over `target` does, the way a phone's home
 * screen does it: on a loose agent, a folder of the two; on an agent already in a
 * folder, or on a folder, into that folder; on the loose list, out of its folder.
 * Anything that would change nothing — or touch the master — is `none`.
 */
export function folderDrop(draggedId: string, target: FolderDropTarget | null, agents: ListAgent[]): FolderDrop {
  const dragged = agents.find((agent) => agent.id === draggedId);
  if (!dragged || dragged.kind === "master" || !target) return { kind: "none" };
  if (target.kind === "loose") return dragged.folderId ? { kind: "move", agentId: dragged.id, folderId: null } : { kind: "none" };
  if (target.kind === "folder") {
    return dragged.folderId === target.id ? { kind: "none" } : { kind: "move", agentId: dragged.id, folderId: target.id };
  }
  const onto = agents.find((agent) => agent.id === target.id);
  if (!onto || onto.id === dragged.id || onto.kind === "master") return { kind: "none" };
  if (onto.folderId) {
    return dragged.folderId === onto.folderId ? { kind: "none" } : { kind: "move", agentId: dragged.id, folderId: onto.folderId };
  }
  return { kind: "create", agentIds: [onto.id, dragged.id] };
}

/** Folders that still hold an agent after `agents` changed: the server deletes the rest. */
const withoutEmpty = (folders: AgentFolder[], agents: Pick<ListAgent, "id" | "folderId">[]): AgentFolder[] =>
  folders
    .map((folder) => ({ ...folder, agentIds: agents.filter((agent) => agent.folderId === folder.id).map((agent) => agent.id) }))
    .filter((folder) => folder.agentIds.length > 0);

/** The cache after moving agents into `folderId` (null: out of any), before the server answers. */
export function applyFolderMove<A extends Pick<ListAgent, "id" | "folderId">>(
  agents: A[],
  folders: AgentFolder[],
  agentIds: string[],
  folderId: string | null,
  /** A folder being made by this move, not yet in the cache. */
  made?: AgentFolder,
): { agents: A[]; folders: AgentFolder[] } {
  const ids = new Set(agentIds);
  const next = agents.map((agent) => (ids.has(agent.id) ? { ...agent, folderId } : agent));
  return { agents: next, folders: withoutEmpty(made ? [...folders, made] : folders, next) };
}

/** A temporary folder for the optimistic cache while the server makes the real one. */
export const pendingFolder = (folders: AgentFolder[], name: string): AgentFolder => ({
  id: `pending_${Date.now()}`,
  name,
  position: folders.reduce((max, folder) => Math.max(max, folder.position), -1) + 1,
  createdAt: new Date().toISOString(),
  agentIds: [],
});

/* --------------------------------------------- collapsed folders, per device */

export const COLLAPSED_FOLDERS_KEY = "automation.agentFolders.collapsed";

export function readCollapsedFolders(storage: Pick<Storage, "getItem"> | undefined = globalThis.localStorage): Set<string> {
  try {
    const parsed: unknown = JSON.parse(storage?.getItem(COLLAPSED_FOLDERS_KEY) ?? "[]");
    return new Set(Array.isArray(parsed) ? parsed.filter((id): id is string => typeof id === "string") : []);
  } catch {
    return new Set();
  }
}

export function writeCollapsedFolders(ids: ReadonlySet<string>, storage: Pick<Storage, "setItem"> | undefined = globalThis.localStorage): void {
  try {
    storage?.setItem(COLLAPSED_FOLDERS_KEY, JSON.stringify([...ids]));
  } catch {
    // Private mode: the folder simply opens again next time.
  }
}
